import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { DataTable, Button } from "@life-line-lab/components-react";
import { Row, Col, Tooltip } from "antd";
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import AssignDriverBoxesModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/assign-driver-boxes-modal";
import { BasicModal } from "@life-line-lab/components-react";
import useSession from "Hooks/session.hook";
import useAccessControlList from "Hooks/access-control.hook";
import useQuery from "Hooks/query.hook";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";

interface BoxesViewProps {
  info: any;
  parcelDetails: any[];
  vehicleOptions: any[];
  onAssignDriver: (input: any) => void
}

const ParcelTrackingBoxesViewComponent: React.FC<BoxesViewProps> = ({
  info,
  parcelDetails,
  vehicleOptions,
  onAssignDriver,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { roleTypes } = useSession();
  const query = useQuery();
  const initialModal: any = {
    open: false,
  };

  const onBack = () => {
    // if (subTab)
    //   // may need to improve if tabKey is passed
    //   history.push(`/delivery-management/parcel-tracking?tabKey=4&subTab=${subTab}`);
    // else history.goBack();

    const redirect: string | undefined = query.get("redirect") || undefined;
    const previousUrl: string | undefined = query.get("from") || undefined;
    previousUrl && !redirect
      ? history.push(decodeURIComponent(previousUrl))
      : history.goBack();
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });

  const showUpdateOption = permission.rights.includes("UPDATE");

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleOpenAssignDriver = () => {
    setCallModal(
      <AssignDriverBoxesModal
        onSubmit={handleAssignDriver}
        vehicleOptions={vehicleOptions}
        boxInfo={info}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const handleAssignDriver = (input: any) => {
    onAssignDriver(input);
    handleResetModalState();
  };

  const columns: any = [
    {
      title: t("label.orderNo").toLocaleUpperCase(),
      dataIndex: "orderNo",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <div>{record?.orders?.orderCode} </div>;
      },
    },
    {
      title: t("label.parcelTrackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div>
            {record?.parcelList?.trackingNumber}{" "}
            {record?.parcelList?.status == "DROPPED_OFF" && (
              <span className="unscanned-alert">
                <Tooltip title="This parcel has not been scanned.">
                  <ExclamationCircleOutlined />
                </Tooltip>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t("label.deliveryType").toLocaleUpperCase(),
      dataIndex: "additionalCharges",
      width: 75,
      ellipsis: true,
      render: (_: any, record: any, text: any, index: any) => {
        return (
          <>
            {record?.orders?.additionalCharges?.map(
              (charge) =>
                charge.type == "DELIVERY_TYPE" && <span>{charge.name}</span>,
            )}
          </>
        );
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: ["orders", "senderInfo", "fullName"],
      ellipsis: true,
    },
    {
      title: t("label.recipientAddress").toLocaleUpperCase(),
      dataIndex: ["orders"],
      ellipsis: true,
      render: (_: any, record: any) => {
        const addressDetails = record?.orders?.receiverInfo?.address;

        return (
          <div>
            <div>
              {addressDetails?.unit && `${addressDetails?.unit},`}{" "}
              {addressDetails?.street1 && `${addressDetails?.street1},`}
            </div>
            <div>
              {addressDetails?.street2 && `${addressDetails?.street2},`}
            </div>
            <div>
              {addressDetails?.postcode && `${addressDetails?.postcode},`}{" "}
              {addressDetails?.city && `${addressDetails?.city},`}
            </div>
            <div>{addressDetails?.state && `${addressDetails?.state},`}</div>
          </div>
        );
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: ["orders", "orderStatusInfo", "orderStatus"],
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
  ];

  const driverColumns: any = [
    {
      title: t("label.driverCompany").toLocaleUpperCase(),
      dataIndex: ["assignedDriver"],
      width: 60,
      ellipsis: true,
      render: (assignedDriver: string) => {
        return (
          <span>
            {assignedDriver
              ? assignedDriver === "THIRD_PARTY"
                ? "3rd Party"
                : "Diolko"
              : "Not assigned"}
          </span>
        );
      },
    },
    {
      title: t("label.driverName").toLocaleUpperCase(),
      dataIndex: ["assignedDriverData", "fullName"],
      width: 75,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {record.assignedDriverData
              ? record.assignedDriverData?.fullName
              : record.assignedDriver || "Not assigned"}
          </span>
        );
      },
    },
  ];

  const weightloadColumns = [
    {
      title: t("label.weightLoadFactor").toLocaleUpperCase(),
      dataIndex: "weightLoadFactor",
      width: 60,
      ellipsis: true,
    },
    {
      title: t("label.volumeLoadFactor").toLocaleUpperCase(),
      dataIndex: "volumeLoadFactor",
      width: 75,
      ellipsis: true,
    },
  ];

  return (
    <div className="dm-put-container">
      <div className="dm-put-header">
        <div className="bvc-title-container">
          <div className="bvc-title">{info?.boxCode}</div>
          <span
            className="status-indicator"
            style={getStatusStyle(info?.status)}
          >
            {info?.status && toCapitalisedSpaced(info?.status)}
          </span>
        </div>
        <div className="put-close">
          <CloseCircleOutlined
            style={{ fontSize: "20px" }}
            onClick={() => {
              onBack();
            }}
            className="cursor-pointer back-action right"
          />
        </div>
      </div>
      <div className="dm-divider"></div>
      <div className="dm-put-content dm-bvc-outer">
        <label className="title"> {t("label.parcelDetails")} </label>
        <div className="dm-bvc-content">
          <DataTable
            rowKey={(record: any) => {
              return record._id;
            }}
            columns={columns}
            dataSource={parcelDetails}
            scroll={{ x: true }}
          />
        </div>
      </div>
      <div className="dm-put-content dm-bvc-outer">
        <label className="title"> {t("label.driverDetails")} </label>
        <Row gutter={[16, 0]}>
          <Col flex="1">
            <DataTable
              rowKey={(record: any) => {
                return record._id;
              }}
              columns={driverColumns}
              dataSource={[info]}
            />
          </Col>
          <Col>
            {showUpdateOption && info.status != "CLOSED" && (
              <Button
                isLoading={status === "loading"}
                type="primary"
                label={t("action.assignBoxDriver")}
                size="large"
                onClick={handleOpenAssignDriver}
              />
            )}
          </Col>
        </Row>
      </div>
      <div className="dm-put-content dm-bvc-outer">
        <label className="title"> {t("label.loadFactors")} </label>
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          columns={weightloadColumns}
          dataSource={[info]}
        />
      </div>
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default ParcelTrackingBoxesViewComponent;
